/**
 * CapabilityCard — collapsible form for a single ProductCatalogue
 * capability. Nested inside PillarView's sortable list.
 *
 * Phase 44.5.
 */

import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import RepeatableTextList from "./RepeatableTextList.jsx";
import { hasTodo, capabilityStatus } from "./catalogueHelpers.js";

const EFFORT_OPTIONS = ["low", "medium", "high", "TODO"];

const STATUS_LABEL = {
  complete: "Complete",
  todo: "Has TODOs",
  empty: "Empty",
};

export default function CapabilityCard({ capability, onChange, onRemove, id }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const [expanded, setExpanded] = useState(false);
  const status = capabilityStatus(capability);

  const update = (key, value) =>
    onChange({ ...capability, [key]: value });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`capability-card capability-card--${status}`}
    >
      <div className="capability-card__header">
        <button
          type="button"
          className="capability-card__drag"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          ⋮⋮
        </button>
        <button
          type="button"
          className="capability-card__toggle"
          onClick={() => setExpanded((x) => !x)}
          aria-expanded={expanded}
        >
          <span className={`capability-card__chevron ${expanded ? "capability-card__chevron--open" : ""}`}>
            ▸
          </span>
          <span className="capability-card__name">
            {capability?.name?.trim() || <em className="muted">Untitled capability</em>}
          </span>
          <span className={`capability-card__status capability-card__status--${status}`}>
            {STATUS_LABEL[status]}
          </span>
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm capability-card__delete"
          onClick={() => {
            if (window.confirm(`Delete capability "${capability?.name || "Untitled"}"?`)) {
              onRemove();
            }
          }}
          aria-label="Delete capability"
        >
          Delete
        </button>
      </div>

      {expanded && (
        <div className="capability-card__body">
          <label className="setup-field">
            <span className="setup-field__label">Name</span>
            <input
              type="text"
              value={capability?.name || ""}
              onChange={(e) => update("name", e.target.value)}
              maxLength={100}
              className="setup-field__input"
            />
          </label>

          <label className="setup-field">
            <span className="setup-field__label">What it does</span>
            <textarea
              className={`setup-field__input ${hasTodo(capability?.what_it_does) ? "setup-field__input--todo" : ""}`}
              value={capability?.what_it_does || ""}
              onChange={(e) => update("what_it_does", e.target.value)}
              rows={3}
              placeholder="Plain-English description. Enter TODO if unknown."
            />
            {hasTodo(capability?.what_it_does) && (
              <span className="setup-field__todo-badge">TODO</span>
            )}
          </label>

          <RepeatableTextList
            label="Problems solved"
            items={capability?.problems_solved || []}
            onChange={(v) => update("problems_solved", v)}
            addLabel="Add problem"
          />
          <RepeatableTextList
            label="Replaces"
            items={capability?.replaces || []}
            onChange={(v) => update("replaces", v)}
            addLabel="Add"
            helpText="Tools this capability displaces."
          />
          <RepeatableTextList
            label="Reduces"
            items={capability?.reduces || []}
            onChange={(v) => update("reduces", v)}
            addLabel="Add"
            helpText="Cost / risk / effort it reduces."
          />
          <RepeatableTextList
            label="Partners with"
            items={capability?.partners_with || []}
            onChange={(v) => update("partners_with", v)}
            addLabel="Add"
            helpText="Tools it co-exists with rather than replaces."
          />
          <RepeatableTextList
            label="Example competitors"
            items={capability?.example_competitors || []}
            onChange={(v) => update("example_competitors", v)}
            addLabel="Add"
          />
          <RepeatableTextList
            label="Target personas"
            items={capability?.target_personas || []}
            onChange={(v) => update("target_personas", v)}
            addLabel="Add"
          />

          <div className="capability-card__row">
            <label className="setup-field">
              <span className="setup-field__label">Effort</span>
              <select
                value={capability?.effort || "TODO"}
                onChange={(e) => update("effort", e.target.value)}
                className="setup-field__input"
              >
                {EFFORT_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="setup-field">
              <span className="setup-field__label">Value return</span>
              <select
                value={capability?.value_return || "TODO"}
                onChange={(e) => update("value_return", e.target.value)}
                className="setup-field__input"
              >
                {EFFORT_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

